/**
 * Search Index Module
 *
 * Handles lazy-loading and caching of the blog search index,
 * plus search functionality with substring matching.
 */

if (typeof FN == 'undefined') FN = {};

// Module-level cache variables
let searchIndex = null;      // Stores loaded posts array
let isLoading = false;        // Prevents duplicate requests
let loadPromise = null;       // Allows multiple callers to await same load

/**
 * Load search index from JSON endpoint
 * Uses fetch with default cache mode for automatic ETag handling
 * @returns {Promise<Array|null>} Array of posts or null on error
 */
async function loadSearchIndex() {
  // Return cached index if already loaded
  if (searchIndex) {
    return searchIndex;
  }

  // If already loading, return the existing promise
  if (isLoading && loadPromise) {
    return loadPromise;
  }

  // Start loading
  isLoading = true;
  loadPromise = (async () => {
    try {
      // Fetch with default cache mode - browser handles ETags automatically
      const searchIndexURL = FN.searchIndexURL || 'search-index.json';
      const response = await fetch(searchIndexURL);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Cache the posts array
      searchIndex = data.posts || [];

      return searchIndex;
    } catch (error) {
      console.error('Failed to load search index:', error);
      return null;
    } finally {
      isLoading = false;
    }
  })();

  return loadPromise;
}

/**
 * Escape special regex characters in user input
 * Prevents regex errors when searching for special characters like "(test)"
 * @param {string} string - User input to escape
 * @returns {string} Escaped string safe for RegExp
 */
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Search posts by title substring matching with relevance scoring
 * Returns up to 4 most relevant matches with highlighted titles
 * Prioritizes: 1) word boundary matches, 2) start-of-title matches, 3) substring matches
 * @param {string} query - Search query (min 3 chars)
 * @returns {Array} Array of matching posts with highlightedTitle property
 */
function searchPosts(query) {
  // Return empty if index not loaded yet
  if (!searchIndex) {
    return [];
  }

  // Validate query length
  const searchTerm = query.trim();
  if (searchTerm.length < 3) {
    return [];
  }

  // Case-insensitive matching
  const lowerQuery = searchTerm.toLowerCase();

  // Filter and score matches
  const matches = searchIndex
    .filter(post => post.title.toLowerCase().includes(lowerQuery))
    .map(post => {
      const lowerTitle = post.title.toLowerCase();
      let score = 0;

      // Highest priority: Exact word boundary match (e.g., "tower" in "Tower for Mac")
      const wordBoundaryRegex = new RegExp(`\\b${escapeRegExp(lowerQuery)}\\b`, 'i');
      if (wordBoundaryRegex.test(lowerTitle)) {
        score += 100;
      }

      // High priority: Match at start of title
      if (lowerTitle.startsWith(lowerQuery)) {
        score += 50;
      }

      // Base score: Any substring match (already filtered above)
      score += 1;

      return { post, score };
    })
    // Sort by score (descending), maintaining recency for ties
    .sort((a, b) => b.score - a.score)
    // Take top 4 results
    .slice(0, 4)
    .map(item => item.post);

  // Add highlighted title to each result
  const escapedQuery = escapeRegExp(searchTerm);
  const regex = new RegExp(escapedQuery, 'gi'); // global, case-insensitive

  return matches.map(post => ({
    title: post.title,
    url: post.url,
    highlightedTitle: post.title.replace(regex, match => `<mark>${match}</mark>`)
  }));
}

// Export to FN namespace
FN.searchIndex = {
  load: loadSearchIndex,
  search: searchPosts
};
